/**
 * Move definition schema. ARCHITECTURE.md §7.5.
 *
 * Move data is structured, inspectable data — never behaviour buried in animation callbacks.
 * `defineMove()` fills defaults, THROWS on unknown keys (so a typo cannot silently disable a
 * mechanic), and derives simulation-tick fields from the authored 60 Hz frame data.
 *
 * Authoring convention: `startup`, `active`, `recovery`, `hitstun`, `blockstun`, and every
 * hitbox window are in 60 Hz frames (f60) — the numbers players and frame-data charts use.
 * The loader multiplies by 2 to get the 120 Hz simulation ticks the engine runs on.
 *
 * Three-free: runs in Node for headless combat tests.
 */

import { F60_TO_TICKS, MIN_COMBO_SCALE } from '../../core/constants.js';

export const CATEGORIES = Object.freeze([
  'strike', 'throw', 'command-throw', 'special', 'stance', 'finisher', 'movement', 'defence',
]);

export const HIT_LEVELS = Object.freeze(['high', 'mid', 'special-mid', 'low', 'unblockable', 'throw']);

export const KNOCKDOWN_KINDS = Object.freeze(['none', 'soft', 'hard']);

/**
 * Every legal key with its default. `defineMove` rejects anything not listed here, which is
 * the mechanism that makes a misspelled `wallSplat` a build failure instead of a silent bug.
 */
const DEFAULTS = Object.freeze({
  id: '',
  name: '',
  input: '',
  category: 'strike',

  startup: 12,
  active: 2,
  recovery: 14,
  /** [openF60, closeF60] inclusive window in which `cancels` targets may be buffered. */
  cancelWindow: null,
  cancels: null,
  followUpOf: '',
  /** Frames after cancelWindow opens during which the follow-up may be delayed. */
  delayableUntil: 0,

  level: 'mid',
  damage: 12,
  chipDamage: 0,
  hitstun: 16,
  blockstun: 8,
  /** Authored expectations; validated against the derived values. -999 means "derive it". */
  hitAdvantage: -999,
  blockAdvantage: -999,

  pushbackHit: 0.14,
  pushbackBlock: 0.2,
  selfPushbackBlock: 0.1,

  /** Forward/vertical movement the move itself applies to the attacker, in metres. */
  moveForward: 0,
  moveUp: 0,

  hitboxes: null,
  throwBox: null,

  counterHit: null,
  onHit: null,
  armor: null,
  invuln: null,

  scaling: null,
  meterGain: null,
  meterCost: 0,

  stanceRequired: '',
  stanceEnter: '',

  /** Horizontal tracking applied during startup to face a sidestepping opponent, 0..1. */
  tracking: 0.5,
  /** Can this move hit a grounded opponent? */
  hitsGrounded: false,
  /** Can this move hit an airborne opponent? Most lows cannot. */
  hitsAir: true,
  /** Juggle-only: connects only while the defender is already airborne. */
  juggleOnly: false,
  /** Attacker becomes airborne for this move (jump attacks). */
  airborneAttacker: false,

  anim: '',
  sfx: null,
  fx: null,
  tags: null,
  aiHints: null,
});

/**
 * A hitbox is a capsule authored in FIGHTER LOCAL SPACE: origin at the fighter's ground
 * point, +X forward (mirrored by `facing`), +Y up, +Z the fighter's left. Metres.
 *
 * Gameplay collision uses these numbers only — never the visual mesh (ARCHITECTURE.md §4).
 * `joint` is a presentation hint: it tells FX where to spawn the impact and tells the
 * animation system which limb must visually occupy the capsule. The hitbox overlay draws
 * the capsule and the limb together so a mismatch is immediately visible.
 *
 * (ox,oy,oz) is the capsule start. (ex,ey,ez) is the end; omit for a sphere.
 */
const HITBOX_DEFAULTS = Object.freeze({
  from: 0,
  to: 0,
  joint: 'handR',
  ox: 0,
  oy: 1.2,
  oz: 0,
  ex: null,
  ey: null,
  ez: null,
  r: 0.11,
  tracking: 0.5,
  priority: 1,
});

const THROWBOX_DEFAULTS = Object.freeze({
  from: 4,
  to: 6,
  ox: 0.32,
  oy: 0.95,
  oz: 0,
  r: 0.42,
  /** Highest opponent state height this throw can catch: 'stand' | 'crouch' | 'any'. */
  catches: 'stand',
});

const COUNTER_DEFAULTS = Object.freeze({
  damageMul: 1.2,
  hitstunBonus: 5,
  forcesState: '',
});

const ONHIT_DEFAULTS = Object.freeze({
  launch: 0,
  wallSplat: false,
  knockdown: 'none',
  juggleGravityMul: 1,
  /** Extra horizontal velocity given to the defender, m/s, positive = away. */
  knockbackX: 0,
  /** Forces the defender into a specific state, overriding the default hitstun choice. */
  forcesState: '',
  /** Ticks of extra hitstop on this move specifically; 0 = derive from damage. */
  hitstop: 0,
});

const ARMOR_DEFAULTS = Object.freeze({ from: 0, to: 0, hits: 0, damageMul: 0 });
const INVULN_DEFAULTS = Object.freeze({ from: 0, to: 0, levels: null });
const SCALING_DEFAULTS = Object.freeze({ starterValue: 1.0, cost: 0.1, minScale: MIN_COMBO_SCALE });
const METER_DEFAULTS = Object.freeze({ hit: 5, block: 2, whiff: 1 });
const SFX_DEFAULTS = Object.freeze({ swing: 'swing-light', hit: 'impact-light', block: 'guard-light' });
const FX_DEFAULTS = Object.freeze({ hit: 'spark-light', trail: '' });
const AIHINT_DEFAULTS = Object.freeze({ range: 1.0, risk: 0.3, use: null });

function assertKeys(obj, defaults, where, path) {
  for (const k in obj) {
    if (!(k in defaults)) {
      throw new Error(
        `Move "${where}": unknown key "${path}${k}". Legal keys: ${Object.keys(defaults).join(', ')}`,
      );
    }
  }
}

function fill(obj, defaults, where, path) {
  if (obj === null || obj === undefined) return { ...defaults };
  assertKeys(obj, defaults, where, path);
  return { ...defaults, ...obj };
}

/**
 * Normalises and validates one move definition.
 * @param {object} raw authored move data
 * @returns {object} frozen, fully-populated move with derived tick fields
 */
export function defineMove(raw) {
  const where = raw.id || '(missing id)';
  assertKeys(raw, DEFAULTS, where, '');

  const m = { ...DEFAULTS, ...raw };

  // --- required scalars ---
  if (!m.id) throw new Error('Move is missing "id".');
  if (!/^[a-z]{2}-[a-z0-9-]+$/.test(m.id)) {
    throw new Error(`Move "${m.id}": id must match /^[a-z]{2}-[a-z0-9-]+$/ (fighter prefix + slug).`);
  }
  if (!m.name) throw new Error(`Move "${m.id}": missing "name".`);
  if (!m.anim) throw new Error(`Move "${m.id}": missing "anim" (animation identifier).`);
  if (!CATEGORIES.includes(m.category)) {
    throw new Error(`Move "${m.id}": category "${m.category}" not in ${CATEGORIES.join('|')}.`);
  }
  if (!HIT_LEVELS.includes(m.level)) {
    throw new Error(`Move "${m.id}": level "${m.level}" not in ${HIT_LEVELS.join('|')}.`);
  }
  for (const k of ['startup', 'active', 'recovery', 'hitstun', 'blockstun', 'damage']) {
    if (typeof m[k] !== 'number' || !Number.isFinite(m[k]) || m[k] < 0) {
      throw new Error(`Move "${m.id}": "${k}" must be a finite number >= 0, got ${m[k]}.`);
    }
  }
  if (m.category === 'strike' && m.active < 1) {
    throw new Error(`Move "${m.id}": a strike needs at least 1 active frame.`);
  }

  // --- nested objects ---
  m.counterHit = fill(m.counterHit, COUNTER_DEFAULTS, m.id, 'counterHit.');
  m.onHit = fill(m.onHit, ONHIT_DEFAULTS, m.id, 'onHit.');
  m.armor = fill(m.armor, ARMOR_DEFAULTS, m.id, 'armor.');
  m.invuln = fill(m.invuln, INVULN_DEFAULTS, m.id, 'invuln.');
  m.scaling = fill(m.scaling, SCALING_DEFAULTS, m.id, 'scaling.');
  m.meterGain = fill(m.meterGain, METER_DEFAULTS, m.id, 'meterGain.');
  m.sfx = fill(m.sfx, SFX_DEFAULTS, m.id, 'sfx.');
  m.fx = fill(m.fx, FX_DEFAULTS, m.id, 'fx.');
  m.aiHints = fill(m.aiHints, AIHINT_DEFAULTS, m.id, 'aiHints.');
  m.invuln.levels = m.invuln.levels || [];
  m.aiHints.use = m.aiHints.use || [];
  m.tags = m.tags || [];
  m.cancels = m.cancels || [];

  if (!KNOCKDOWN_KINDS.includes(m.onHit.knockdown)) {
    throw new Error(`Move "${m.id}": onHit.knockdown "${m.onHit.knockdown}" not in ${KNOCKDOWN_KINDS.join('|')}.`);
  }
  if (m.scaling.minScale < MIN_COMBO_SCALE - 1e-9) {
    throw new Error(`Move "${m.id}": scaling.minScale ${m.scaling.minScale} below global floor ${MIN_COMBO_SCALE}.`);
  }
  for (const lv of m.invuln.levels) {
    if (!HIT_LEVELS.includes(lv)) throw new Error(`Move "${m.id}": invuln.levels has unknown level "${lv}".`);
  }

  // --- hitboxes ---
  const isThrow = m.category === 'throw' || m.category === 'command-throw';
  if (isThrow) {
    m.level = 'throw';
    m.throwBox = fill(m.throwBox, THROWBOX_DEFAULTS, m.id, 'throwBox.');
    if (!['stand', 'crouch', 'any'].includes(m.throwBox.catches)) {
      throw new Error(`Move "${m.id}": throwBox.catches must be stand|crouch|any.`);
    }
    m.hitboxes = [];
  } else {
    m.throwBox = null;
    const boxes = m.hitboxes || [];
    if (m.category === 'strike' && boxes.length === 0) {
      throw new Error(`Move "${m.id}": a strike needs at least one hitbox.`);
    }
    m.hitboxes = boxes.map((hb, i) => {
      const box = fill(hb, HITBOX_DEFAULTS, m.id, `hitboxes[${i}].`);
      if (box.to < box.from) {
        throw new Error(`Move "${m.id}": hitboxes[${i}] window [${box.from},${box.to}] is inverted.`);
      }
      if (box.r <= 0) throw new Error(`Move "${m.id}": hitboxes[${i}].r must be > 0.`);
      // Omitted endpoint means a sphere: collapse the capsule onto its start point.
      box.isCapsule = box.ex !== null || box.ey !== null || box.ez !== null;
      if (box.ex === null) box.ex = box.ox;
      if (box.ey === null) box.ey = box.oy;
      if (box.ez === null) box.ez = box.oz;
      if (box.oy < 0 || box.ey < 0) {
        throw new Error(`Move "${m.id}": hitboxes[${i}] has a negative height — oy/ey are metres above ground.`);
      }
      return box;
    });
  }

  // --- cancel window ---
  const total = m.startup + m.active + m.recovery;
  if (m.cancelWindow) {
    if (!Array.isArray(m.cancelWindow) || m.cancelWindow.length !== 2) {
      throw new Error(`Move "${m.id}": cancelWindow must be [openF60, closeF60].`);
    }
    const [a, b] = m.cancelWindow;
    if (b < a) throw new Error(`Move "${m.id}": cancelWindow [${a},${b}] is inverted.`);
    if (b > total) {
      throw new Error(`Move "${m.id}": cancelWindow closes at ${b} but the move is only ${total} frames long.`);
    }
    if (m.cancels.length === 0) {
      throw new Error(`Move "${m.id}": has a cancelWindow but an empty "cancels" list.`);
    }
  } else if (m.cancels.length > 0) {
    // A cancel list with no explicit window defaults to "from the first active frame to the end".
    m.cancelWindow = [m.startup + 1, total];
  } else {
    m.cancelWindow = [0, 0];
  }

  // --- derived frame advantage (ARCHITECTURE.md §7.5) ---
  // The attacker's first active frame is `startup + 1`; after it connects the attacker still
  // owes (active - 1) + recovery frames before acting again.
  const attackerCommitted = m.active + m.recovery - 1;
  const derivedHitAdv = m.hitstun - attackerCommitted;
  const derivedBlockAdv = m.blockstun - attackerCommitted;

  if (m.hitAdvantage !== -999 && m.hitAdvantage !== derivedHitAdv) {
    throw new Error(
      `Move "${m.id}": authored hitAdvantage ${m.hitAdvantage} != derived ${derivedHitAdv} ` +
        `(hitstun ${m.hitstun} - (active ${m.active} + recovery ${m.recovery} - 1)). ` +
        'Fix the frame data or drop the authored value.',
    );
  }
  if (m.blockAdvantage !== -999 && m.blockAdvantage !== derivedBlockAdv) {
    throw new Error(
      `Move "${m.id}": authored blockAdvantage ${m.blockAdvantage} != derived ${derivedBlockAdv}.`,
    );
  }
  m.hitAdvantage = derivedHitAdv;
  m.blockAdvantage = derivedBlockAdv;
  m.totalFrames = total;

  // --- derived tick fields: the numbers the simulation actually runs on ---
  const K = F60_TO_TICKS;
  m.tStartup = Math.round(m.startup * K);
  m.tActive = Math.round(m.active * K);
  m.tRecovery = Math.round(m.recovery * K);
  m.tTotal = m.tStartup + m.tActive + m.tRecovery;
  m.tHitstun = Math.round(m.hitstun * K);
  m.tBlockstun = Math.round(m.blockstun * K);
  m.tCancelOpen = Math.round(m.cancelWindow[0] * K);
  m.tCancelClose = Math.round(m.cancelWindow[1] * K);
  m.tDelayableUntil = Math.round(m.delayableUntil * K);
  m.tCounterHitstunBonus = Math.round(m.counterHit.hitstunBonus * K);
  for (const hb of m.hitboxes) {
    hb.tFrom = Math.round((hb.from - 1) * K); // f60 frame N spans ticks [(N-1)*2, N*2)
    hb.tTo = Math.round(hb.to * K);
  }
  if (m.throwBox) {
    m.throwBox.tFrom = Math.round((m.throwBox.from - 1) * K);
    m.throwBox.tTo = Math.round(m.throwBox.to * K);
  }
  m.tArmorFrom = Math.round((m.armor.from - 1) * K);
  m.tArmorTo = Math.round(m.armor.to * K);
  m.tInvulnFrom = Math.round((m.invuln.from - 1) * K);
  m.tInvulnTo = Math.round(m.invuln.to * K);

  m.isThrow = isThrow;
  m.isSpecial = m.category === 'special' || m.category === 'finisher';
  m.isLauncher = m.onHit.launch > 0;
  m.fighterPrefix = m.id.slice(0, 2);

  // Hitboxes must all fall inside the declared active window, otherwise the frame data
  // shown in training mode would lie about when the move can actually hit.
  for (let i = 0; i < m.hitboxes.length; i++) {
    const hb = m.hitboxes[i];
    if (hb.from < m.startup + 1 || hb.to > m.startup + m.active) {
      throw new Error(
        `Move "${m.id}": hitboxes[${i}] window [${hb.from},${hb.to}] falls outside the active ` +
          `window [${m.startup + 1},${m.startup + m.active}]. Frame data must match hit detection.`,
      );
    }
  }
  if (m.throwBox) {
    if (m.throwBox.from < m.startup + 1 || m.throwBox.to > m.startup + Math.max(1, m.active)) {
      throw new Error(
        `Move "${m.id}": throwBox window [${m.throwBox.from},${m.throwBox.to}] falls outside the ` +
          `active window [${m.startup + 1},${m.startup + Math.max(1, m.active)}].`,
      );
    }
  }

  return m;
}

/**
 * Builds a move table from an array of authored definitions.
 * Validates uniqueness, cancel-target existence, and string-tree consistency.
 * @param {Array<object>} list
 * @returns {{ byId: Map<string, object>, all: Array<object> }}
 */
export function buildMoveTable(list) {
  const byId = new Map();
  const all = [];
  for (const raw of list) {
    const move = defineMove(raw);
    if (byId.has(move.id)) throw new Error(`Duplicate move id "${move.id}".`);
    byId.set(move.id, move);
    all.push(move);
  }
  // Cross references are resolved after every move exists.
  for (const move of all) {
    for (const target of move.cancels) {
      if (!byId.has(target)) {
        throw new Error(`Move "${move.id}": cancels into unknown move "${target}".`);
      }
    }
    if (move.followUpOf && !byId.has(move.followUpOf)) {
      throw new Error(`Move "${move.id}": followUpOf unknown move "${move.followUpOf}".`);
    }
    if (move.followUpOf) {
      const parent = byId.get(move.followUpOf);
      if (!parent.cancels.includes(move.id)) {
        throw new Error(
          `Move "${move.id}": declares followUpOf "${parent.id}" but "${parent.id}".cancels ` +
            'does not list it. String trees must be declared from both ends.',
        );
      }
    }
  }
  return { byId, all };
}

/** Human-readable frame-data line, used by training mode and FRAME_DATA.md. */
export function formatFrameData(move) {
  const adv = (n) => (n >= 0 ? `+${n}` : `${n}`);
  return (
    `${move.name.padEnd(22)} ${move.input.padEnd(12)} ` +
    `i${String(move.startup).padStart(2)} ` +
    `a${String(move.active).padStart(2)} ` +
    `r${String(move.recovery).padStart(2)}  ` +
    `${move.level.padEnd(11)} ` +
    `dmg ${String(move.damage).padStart(3)}  ` +
    `blk ${adv(move.blockAdvantage).padStart(3)}  ` +
    `hit ${adv(move.hitAdvantage).padStart(3)}`
  );
}

export { DEFAULTS as MOVE_DEFAULTS, HITBOX_DEFAULTS, THROWBOX_DEFAULTS };
